import React, {useEffect, useState, useRef} from 'react';
import {useSearchParams, useNavigate} from 'react-router-dom';
import api from '../../api/axios';
import {useAuth} from '../../contexts/AuthContext';
import {usePopupFlow} from '../../hooks/usePopupFlow';
import {
    GoogleIcon,
    CheckIcon,
    AlertCircleIcon,
    ArrowRightIcon,
    XIcon,
} from '../../components/Icons';

export default function AuthPopupNext() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const auth = useAuth();

    const [status, setStatus] = useState('loading'); // 'loading' | 'success' | 'error'
    const [errorMessage, setErrorMessage] = useState('');
    const [userData, setUserData] = useState(null);
    const [countdown, setCountdown] = useState(3);

    const {handleLoginSuccess} = usePopupFlow();

    const hasCalledApiRef = useRef(false);
    const hasOpener = typeof window !== 'undefined' && Boolean(window.opener);

    // 1. Process OAuth code from URL parameters
    useEffect(() => {
        if (hasCalledApiRef.current) {
            return;
        }

        const oauthError = searchParams.get('error') || searchParams.get('error_description');
        if (oauthError) {
            hasCalledApiRef.current = true;
            setErrorMessage(
                oauthError === 'access_denied'
                    ? 'Google sign-in was cancelled or access was denied.'
                    : `Google OAuth error: ${oauthError}`
            );
            setStatus('error');
            return;
        }

        const code = searchParams.get('code');
        if (!code) {
            hasCalledApiRef.current = true;
            setErrorMessage('No authorization code found in the callback URL.');
            setStatus('error');
            return;
        }

        hasCalledApiRef.current = true;

        const exchangeCodeForProfile = async () => {
            try {
                setStatus('loading');
                setErrorMessage('');

                const response = await api.post('/auth/social/google/callback', {code});
                const data = response?.data || {};

                const token = data.access_token || data.accessToken || null;
                const refreshToken = data.refresh_token || data.refreshToken || null;
                const user = data.user || null;

                if (token) {
                    try {
                        localStorage.setItem('access_token', token);
                        if (refreshToken) {
                            localStorage.setItem('refresh_token', refreshToken);
                        }
                        if (user) {
                            localStorage.setItem('user', JSON.stringify(user));
                        }
                    } catch (e) {
                        console.error('Failed to write tokens to localStorage:', e);
                    }

                    // Update multi-account session if available
                    if (auth && typeof auth.addOrUpdateAccount === 'function') {
                        try {
                            auth.addOrUpdateAccount(token, refreshToken, user, true);
                        } catch (e) {
                            console.error('Failed to add account to AuthContext:', e);
                        }
                    }

                    // Notify opener parent window with full payload
                    if (window.opener && typeof window.opener.postMessage === 'function') {
                        try {
                            await handleLoginSuccess(token);
                        } catch (e) {
                            console.error('Failed to dispatch postMessage to opener:', e);
                        }
                    }


                    setUserData(user);
                    setStatus('success');
                } else {
                    setErrorMessage(data.message || 'No access token received from authentication server.');
                    setStatus('error');
                }
            } catch (err) {
                console.error('Error exchanging Google OAuth code:', err);
                const serverMsg =
                    err.response?.data?.message ||
                    err.response?.data?.error ||
                    err.message ||
                    'Failed to authenticate with Google. Please try again.';
                setErrorMessage(serverMsg);
                setStatus('error');
            }
        };

        exchangeCodeForProfile();
    }, [searchParams, auth]);

    // 2. Countdown timer and auto-closure on success
    useEffect(() => {
        if (status !== 'success') return;

        if (countdown <= 0) {
            if (hasOpener) {
                try {
                    window.close();
                } catch (e) {
                    // If window.close() fails or is blocked by browser policy, fallback
                    navigate('/dashboard');
                }
            } else {
                navigate('/dashboard');
            }
            return;
        }

        const timer = setTimeout(() => {
            setCountdown((prev) => prev - 1);
        }, 1000);

        return () => clearTimeout(timer);
    }, [status, countdown, hasOpener, navigate]);

    // Manual actions
    const handleClose = () => {
        if (hasOpener) {
            try {
                window.close();
            } catch (e) {
                navigate('/dashboard');
            }
        } else {
            navigate('/dashboard');
        }
    };

    const handleDashboard = () => {
        if (hasOpener) {
            try {
                window.opener.focus();
                window.close();
            } catch (e) {
                navigate('/dashboard');
            }
        } else {
            navigate('/dashboard');
        }
    };

    const handleRetry = () => {
        navigate('/auth/popup/password');
    };

    // Derive display values
    const displayName = userData?.first_name
        ? `${userData.first_name}${userData.last_name ? ' ' + userData.last_name : ''}`
        : userData?.username || userData?.email || '';

    const avatarLetter = displayName ? displayName.charAt(0).toUpperCase() : 'U';

    return (
        <div className="w-full">
            {/* 1. Loading State */}
            {status === 'loading' && (
                <div className="py-6 flex flex-col items-center justify-center text-center space-y-4">
                    <div className="relative w-16 h-16 flex items-center justify-center">
                        <div
                            className="absolute inset-0 rounded-full border-4 border-indigo-200 dark:border-indigo-900/50 border-t-indigo-600 animate-spin"/>
                        <GoogleIcon className="w-7 h-7 relative z-10"/>
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                            Authenticating with Google
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto">
                            Please wait while we verify your credentials and secure your session...
                        </p>
                    </div>
                    <div className="w-44 h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                        <div className="h-full bg-indigo-600 rounded-full animate-pulse w-2/3 mx-auto"/>
                    </div>
                </div>
            )}

            {/* 2. Success State */}
            {status === 'success' && (
                <div className="py-4 flex flex-col items-center justify-center text-center space-y-5">
                    <div
                        className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-950/60 border border-emerald-300 dark:border-emerald-700 flex items-center justify-center text-emerald-600 dark:text-emerald-400 shadow-sm">
                        <CheckIcon className="w-8 h-8"/>
                    </div>

                    <div className="space-y-1">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                            Authentication Successful!
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            Welcome back{displayName ? `, ${displayName}` : ''}
                        </p>
                    </div>

                    {/* User profile snippet */}
                    <div
                        className="w-full max-w-sm bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-xl p-3.5 flex items-center gap-3 text-left">
                        <div
                            className="w-10 h-10 rounded-full bg-indigo-600 text-white font-bold flex items-center justify-center flex-shrink-0 text-sm shadow-sm">
                            {avatarLetter}
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-slate-900 dark:text-white truncate">
                                {displayName || 'Google Account'}
                            </p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                {userData?.email || userData?.username || (userData?.sso_id ? `SSO: ${userData.sso_id}` : 'Authenticated')}
                            </p>
                        </div>
                        <div className="flex-shrink-0">
                            <GoogleIcon className="w-5 h-5 opacity-90"/>
                        </div>
                    </div>

                    {/* Auto-closing notice */}
                    <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center justify-center gap-2">
                        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"/>
                        <span>
              {hasOpener
                  ? `Closing this window in ${countdown}s...`
                  : `Redirecting to dashboard in ${countdown}s...`}
            </span>
                    </div>

                    {/* Actions */}
                    <div className="w-full pt-2 flex flex-col gap-2">
                        {hasOpener ? (
                            <>
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-medium text-sm rounded-lg shadow-sm transition-colors cursor-pointer"
                                >
                                    Close Window Now
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDashboard}
                                    className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline py-1"
                                >
                                    Switch to Dashboard
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                onClick={handleDashboard}
                                className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-medium text-sm rounded-lg shadow-sm transition-colors cursor-pointer flex items-center justify-center gap-2"
                            >
                                <span>Go to Dashboard</span>
                                <ArrowRightIcon className="w-4 h-4"/>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* 3. Error State */}
            {status === 'error' && (
                <div className="py-4 flex flex-col items-center justify-center text-center space-y-5">
                    <div
                        className="w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-950/60 border border-rose-300 dark:border-rose-700 flex items-center justify-center text-rose-600 dark:text-rose-400 shadow-sm">
                        <AlertCircleIcon className="w-8 h-8"/>
                    </div>

                    <div className="space-y-1">
                        <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                            Authentication Failed
                        </h2>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                            We couldn't complete the sign-in with Google.
                        </p>
                    </div>

                    {/* Error Message Details */}
                    <div
                        className="w-full max-w-sm p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800 text-rose-700 dark:text-rose-300 text-xs text-left flex items-start gap-2">
                        <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5"/>
                        <span className="break-words">{errorMessage}</span>
                    </div>

                    {/* Action buttons */}
                    <div className="w-full space-y-2 pt-2">
                        <button
                            type="button"
                            onClick={handleRetry}
                            className="w-full py-2.5 px-4 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white font-medium text-sm rounded-lg shadow-sm transition-colors cursor-pointer"
                        >
                            Back to Sign In
                        </button>
                        {hasOpener && (
                            <button
                                type="button"
                                onClick={handleClose}
                                className="w-full py-2 px-4 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium text-sm rounded-lg transition-colors cursor-pointer"
                            >
                                Close Window
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}